import { validate as isUuid } from "uuid";
import { ItemType } from "~/utils/enums/common.enums";
import { COPILOT_RESOURCE_ITEM_TYPES, CopilotResourceItemTypeEnum } from "~/utils/enums/copilot/resource-context.enums";
import type { ICopilotResourceContext, ICopilotResourcePageState } from "~/utils/interfaces/copilot-interfaces";
import { hostsTrainingModal } from "~/utils/helpers/trainings/training-modal-host";

const SUPPORTED_ITEM_TYPES = new Set<string>(COPILOT_RESOURCE_ITEM_TYPES);
const PROJECT_USER_ROW_PREFIX = "pr-";

type ResourceRoute = { path: string; query: Record<string, unknown>; params: Record<string, unknown> };
type CopilotResourceContextInput = Omit<ICopilotResourceContext, "itemId"> & {
  itemId?: ICopilotResourceContext["itemId"] | null;
};

/** Copy only the backend's two allowed fields, rejecting unsupported types and malformed IDs. */
export function validCopilotResourceContext(
  context: CopilotResourceContextInput | null | undefined,
): ICopilotResourceContext | null {
  if (
    !context ||
    typeof context.itemType !== "string" ||
    !SUPPORTED_ITEM_TYPES.has(context.itemType) ||
    typeof context.itemId !== "string" ||
    !isUuid(context.itemId)
  )
    return null;

  return { itemType: context.itemType, itemId: context.itemId };
}

function sessionResourceOf(route: ResourceRoute, state: ICopilotResourcePageState) {
  if (
    !["/sessions", "/my-sessions"].includes(route.path) ||
    !state.sessionOpen ||
    !state.session ||
    route.query.id !== state.session.id ||
    route.query.type !== state.session.itemType ||
    (state.session.itemType !== ItemType.SESSION && state.session.itemType !== ItemType.PROJECT)
  )
    return null;

  const itemType = state.session.itemType === ItemType.SESSION ? ItemType.SESSION : ItemType.PROJECT;
  return validCopilotResourceContext({ itemType, itemId: state.session.id });
}

function trainerSessionResourceOf(route: ResourceRoute, state: ICopilotResourcePageState) {
  return route.path === `/trainer-activities/${route.params.sessionId}` &&
    route.params.sessionId === state.trainerSessionId
    ? validCopilotResourceContext({ itemType: ItemType.SESSION, itemId: state.trainerSessionId })
    : null;
}

/** A foreground registration does not remove the underlying session's attendance anchor. */
export function copilotPageSessionIdOf(route: ResourceRoute, state: ICopilotResourcePageState): string | null {
  const session = sessionResourceOf(route, state) ?? trainerSessionResourceOf(route, state);
  return session?.itemType === ItemType.SESSION ? session.itemId : null;
}

function registrationResourceOf(details: ICopilotResourcePageState["registration"]) {
  if (details?.id?.startsWith(PROJECT_USER_ROW_PREFIX)) {
    return validCopilotResourceContext({
      itemType: CopilotResourceItemTypeEnum.PROJECT_USER,
      itemId: details.id.slice(PROJECT_USER_ROW_PREFIX.length),
    });
  }
  return validCopilotResourceContext({ itemType: ItemType.BOOKING, itemId: details?.bookingId });
}

/** Route discriminators and visible detail state must agree, including while the next detail loads. */
export function copilotResourceContextOf(
  route: ResourceRoute,
  state: ICopilotResourcePageState,
): ICopilotResourceContext | null {
  // The existing training editor can also open over another resource through Companion.
  if (state.trainingEditorOpen) {
    if (!state.trainingEditorPrefilled && hostsTrainingModal(route) && route.query.id !== state.trainingEditorId)
      return null;
    return validCopilotResourceContext({
      itemType: CopilotResourceItemTypeEnum.TRAINING,
      itemId: state.trainingEditorId,
    });
  }

  if (route.path === "/catalog")
    return state.trainingPageOpen && route.query.id === state.trainingPageId
      ? validCopilotResourceContext({ itemType: CopilotResourceItemTypeEnum.TRAINING, itemId: state.trainingPageId })
      : null;

  if (route.path === `/requests/${route.params.request_id}`)
    return route.params.request_id === state.requestId
      ? validCopilotResourceContext({ itemType: ItemType.REQUEST, itemId: state.requestId })
      : null;

  if (["/sessions", "/my-sessions"].includes(route.path)) {
    const session = sessionResourceOf(route, state);
    if (!session || !state.registrationOpen) return session;
    const parentId =
      session.itemType === ItemType.SESSION ? state.registration?.sessionId : state.registration?.projectId;
    return parentId === session.itemId ? registrationResourceOf(state.registration) : null;
  }

  if (route.path === "/registrations") {
    if (
      !state.registrationOpen ||
      typeof route.query.details_id !== "string" ||
      route.query.details_id !== state.registration?.id ||
      (route.query.type !== ItemType.SESSION && route.query.type !== ItemType.PROJECT)
    )
      return null;
    return registrationResourceOf(state.registration);
  }

  if (route.path === "/learning-path") {
    const attendee = state.learningPathAttendee;
    return state.registrationOpen &&
      attendee &&
      route.query.learning_path_id === attendee.learningPathId &&
      route.query.attendee_id === attendee.attendeeId &&
      state.registration?.userId === attendee.attendeeId
      ? registrationResourceOf(state.registration)
      : null;
  }

  if (route.path === "/users")
    return state.userOpen && route.query.user_id === state.userId
      ? validCopilotResourceContext({ itemType: ItemType.USER, itemId: state.userId })
      : null;

  if (route.path === "/habilitations")
    return state.habilitationOpen && route.query.user_habilitation_id === state.habilitationId
      ? validCopilotResourceContext({ itemType: ItemType.HABILITATION, itemId: state.habilitationId })
      : null;

  if (["/home", "/my-trainings"].includes(route.path)) {
    if (route.query.booking === "false")
      return state.employeeRequestOpen && route.query.id === state.requestId
        ? validCopilotResourceContext({ itemType: ItemType.REQUEST, itemId: state.requestId })
        : null;
    if (!state.employeeTrainingOpen || route.query.booking !== "true" || typeof route.query.id !== "string")
      return null;
    const detailId = state.registration?.id;
    if (detailId !== route.query.id && detailId !== `tr-${route.query.id}`) return null;
    return registrationResourceOf(state.registration);
  }

  if (route.path === `/date-choice/${route.params.projectUserId}`)
    return route.params.projectUserId === state.projectUserId
      ? validCopilotResourceContext({ itemType: CopilotResourceItemTypeEnum.PROJECT_USER, itemId: state.projectUserId })
      : null;

  return trainerSessionResourceOf(route, state);
}
